import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import ToggleButton from '@mui/material/ToggleButton';
import DialogContent from '@mui/material/DialogContent';
import InputAdornment from '@mui/material/InputAdornment';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import axios, { endpoints } from 'src/utils/axios';
import { normalizeDigits } from 'src/utils/persian-validation';

import { Iconify } from 'src/components/iconify';
import TextField from 'src/components/localized-text-field';
import { AppDialogActions } from 'src/components/custom-dialog';
import { VerificationCodeInput } from 'src/components/verification-code-input';

import { useAuthContext } from 'src/auth/hooks';
import {
  cancelReauthentication,
  completeReauthentication,
  onReauthenticationRequired,
} from 'src/auth/reauthentication-coordinator';

const methodDefinitions = [
  {
    value: 'password',
    label: 'رمز عبور',
    description: 'رمز فعلی حساب',
    icon: 'solar:lock-password-bold-duotone',
  },
  {
    value: 'totp',
    label: 'برنامه رمزساز',
    description: 'کد شش‌رقمی',
    icon: 'solar:smartphone-bold-duotone',
  },
  {
    value: 'recovery',
    label: 'کد بازیابی',
    description: 'کد یک‌بارمصرف',
    icon: 'solar:key-bold-duotone',
  },
];

const methodHints = {
  password: 'رمز عبور فعلی حساب خود را وارد کنید.',
  totp: 'کد شش‌رقمی نمایش‌داده‌شده در برنامه رمزساز را وارد کنید.',
  recovery: 'یکی از کدهای بازیابی استفاده‌نشده را وارد کنید.',
};

function getRequestError(error) {
  const message = error?.message || error?.title;
  return typeof message === 'string' && /[\u0600-\u06ff]/.test(message)
    ? message
    : 'اطلاعات احراز هویت صحیح نیست.';
}

export function ReauthenticationDialog() {
  const { user } = useAuthContext();
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState('password');
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfaStatus, setMfaStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => onReauthenticationRequired(() => setOpen(true)), []);

  useEffect(() => {
    if (!open) return undefined;

    let active = true;
    setStatusLoading(true);
    setStatusError(false);
    axios
      .get(endpoints.mfa)
      .then((response) => {
        if (active) setMfaStatus(response.data);
      })
      .catch(() => {
        if (active) setStatusError(true);
      })
      .finally(() => {
        if (active) setStatusLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open]);

  const methods = methodDefinitions.map((item) => ({
    ...item,
    disabled:
      (item.value === 'totp' && !mfaStatus?.totpAvailable) ||
      (item.value === 'recovery' &&
        (!mfaStatus?.recoveryCodesAvailable || mfaStatus.recoveryCodesRemaining < 1)),
  }));

  const reset = () => {
    setMethod('password');
    setValue('');
    setError('');
    setShowPassword(false);
    setMfaStatus(null);
    setStatusLoading(false);
    setStatusError(false);
  };

  const close = () => {
    if (busy) return;
    setOpen(false);
    reset();
    cancelReauthentication();
  };

  const validate = () => {
    const normalizedValue = normalizeDigits(value).replace(/\s/g, '');
    if (method === 'totp' && !/^\d{6}$/.test(normalizedValue)) {
      setError('کد برنامه رمزساز باید دقیقاً ۶ رقم باشد.');
      return false;
    }
    if (!value) {
      setError(
        method === 'recovery' ? 'واردکردن کد بازیابی الزامی است.' : 'تکمیل این فیلد الزامی است.'
      );
      return false;
    }

    setError('');
    return true;
  };

  const submit = async () => {
    if (!validate()) return;

    const normalizedValue = normalizeDigits(value).replace(/\s/g, '');
    try {
      setBusy(true);
      setError('');
      await axios.post(`${endpoints.mfa}/reauthenticate`, {
        password: method === 'password' ? value : null,
        totpCode: method === 'totp' ? normalizedValue : null,
        recoveryCode: method === 'recovery' ? normalizedValue : null,
      });
      setOpen(false);
      reset();
      completeReauthentication();
    } catch (requestError) {
      setError(getRequestError(requestError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      scroll="paper"
      open={open}
      onClose={close}
      disableEscapeKeyDown={busy}
      PaperProps={{
        component: 'form',
        autoComplete: 'on',
        sx: {
          m: 2,
          width: 'calc(100% - 32px)',
          maxHeight: 'calc(100dvh - 32px)',
          overflowX: 'hidden',
        },
        onSubmit: (event) => {
          event.preventDefault();
          if (!busy) submit();
        },
      }}
    >
      <DialogTitle sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 48,
              height: 48,
              display: 'grid',
              flexShrink: 0,
              placeItems: 'center',
              borderRadius: 1.5,
              color: 'primary.main',
              bgcolor: 'primary.lighter',
            }}
          >
            <Iconify icon="solar:shield-check-bold-duotone" width={28} />
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6">تأیید دوباره هویت</Typography>
            <Typography variant="body2" color="text.secondary">
              برای ادامه امن عملیات، هویت خود را تأیید کنید.
            </Typography>
          </Box>

          <IconButton aria-label="بستن" onClick={close} disabled={busy}>
            <Iconify icon="mingcute:close-line" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 2.5, overflowX: 'hidden' }}>
        <Stack spacing={2.25}>
          <Box
            component="input"
            name="username"
            type="text"
            autoComplete="username"
            value={user?.email || user?.mobile || user?.username || ''}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            sx={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              p: 0,
              border: 0,
              opacity: 0,
              pointerEvents: 'none',
            }}
          />

          <Alert severity="info" variant="outlined">
            پس از تأیید موفق، همین عملیات به‌صورت خودکار ادامه پیدا می‌کند.
          </Alert>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              روش تأیید هویت
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={method}
              disabled={statusLoading}
              onChange={(_, nextMethod) => {
                if (!nextMethod) return;
                setMethod(nextMethod);
                setValue('');
                setError('');
                setShowPassword(false);
              }}
              sx={{
                gap: 1,
                '& .MuiToggleButtonGroup-grouped': {
                  m: 0,
                  minWidth: 0,
                  minHeight: 72,
                  px: 1,
                  border: '1px solid !important',
                  borderColor: 'divider !important',
                  borderRadius: '12px !important',
                },
                '& .Mui-selected': {
                  color: 'primary.dark',
                  borderColor: 'primary.main !important',
                  bgcolor: 'primary.lighter !important',
                },
              }}
            >
              {methods.map((item) => (
                <ToggleButton key={item.value} value={item.value} disabled={item.disabled}>
                  <Stack spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <Iconify icon={item.icon} width={22} />
                    <Typography variant="subtitle2" noWrap>
                      {item.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {item.disabled ? 'فعال نشده' : item.description}
                    </Typography>
                  </Stack>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {statusError && (
            <Alert severity="warning" variant="outlined">
              وضعیت روش‌های دومرحله‌ای دریافت نشد؛ می‌توانید با رمز عبور ادامه دهید.
            </Alert>
          )}

          {method === 'totp' ? (
            <VerificationCodeInput
              key={method}
              value={value}
              error={Boolean(error)}
              helperText={error || methodHints[method]}
              aria-label="کد برنامه رمزساز"
              onChange={(nextValue) => {
                setValue(nextValue);
                if (error) setError('');
              }}
              onBlur={validate}
            />
          ) : (
            <TextField
              key={method}
              autoFocus
              fullWidth
              required
              name={method === 'password' ? 'current-password' : 'one-time-code'}
              type={method === 'password' && !showPassword ? 'password' : 'text'}
              autoComplete={method === 'password' ? 'current-password' : 'one-time-code'}
              label={method === 'password' ? 'رمز عبور فعلی' : 'کد بازیابی یک‌بارمصرف'}
              value={value}
              error={Boolean(error)}
              helperText={error || methodHints[method]}
              onChange={(event) => {
                setValue(event.target.value);
                if (error) setError('');
              }}
              onBlur={validate}
              inputProps={{ dir: 'ltr', inputMode: 'text' }}
              InputProps={
                method === 'password'
                  ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            aria-label={showPassword ? 'پنهان‌کردن رمز عبور' : 'نمایش رمز عبور'}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => setShowPassword((current) => !current)}
                          >
                            <Iconify
                              icon={showPassword ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                            />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                  : undefined
              }
            />
          )}
        </Stack>
      </DialogContent>

      <AppDialogActions>
        <LoadingButton
          type="submit"
          variant="contained"
          loading={busy}
          startIcon={<Iconify icon="solar:shield-check-bold" />}
        >
          تأیید و ادامه عملیات
        </LoadingButton>

        <Button color="inherit" variant="outlined" onClick={close} disabled={busy}>
          انصراف
        </Button>
      </AppDialogActions>
    </Dialog>
  );
}
